import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { NgGlyph } from "@ng-icons/core";
import { PrintingEffects } from "../../State/PrintingEffects";
import { PrintingEntity } from "../../Dto/PrintingModel";
import { PrintingUpsertItem, InspectPrintingItemRequest, UpdatePrintingRequest } from "../../Dto/CreatePrintingRequest";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { VendorApi } from "../../../Vendor/Api/VendorApi";
import { VendorType } from "../../../Vendor/Dto/VendorModel";
import { WarehouseApi } from "../../../Warehouse/Api/WarehouseApi";
import { MaterialApi } from "../../../Material/Api/MaterialApi";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import {
  MaterialAutocompleteOption,
  mergeMaterialAutocompleteOptions,
  toMaterialAutocompleteOption,
} from "../../../../Common/Utility/UtilityMaterialSequence";
import { FileUploaderComponent } from "../../../File/Components/FileUploader/FileUploaderComponent";
import { FileApi } from "../../../File/Api/FileApi";
import { File as UploadedFile } from "../../../File/Dto/FileModel";

@Component({
  selector: "app-printing-edit",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AutocompleteComponent,
    RichTextEditorComponent,
    QtyInputSelectZeroDirective,
    NgGlyph,
    FileUploaderComponent,
  ],
  templateUrl: "./PrintingEditPage.html",
  styleUrl: "./PrintingEditPage.scss",
})
export class PrintingEditPage implements OnInit {
  readonly attachmentFolder = "printings";
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly printingEffects = inject(PrintingEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly vendorApi = inject(VendorApi);
  private readonly warehouseApi = inject(WarehouseApi);
  private readonly materialApi = inject(MaterialApi);
  private readonly fileApi = inject(FileApi);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly printingId = this.route.snapshot.paramMap.get("id") ?? undefined;
  printing: PrintingEntity | null = null;
  isReady = false;
  isSaving = false;
  isAttachmentSaving = false;
  vendorOptions: AutocompleteOption[] = [];
  warehouseOptions: AutocompleteOption[] = [];
  materialSearchOptions: MaterialAutocompleteOption[] = [];
  materialOptionsByIndex: MaterialAutocompleteOption[][] = [];
  selectedVendorId = "";
  selectedWarehouseId = "";
  selectedMaterialIds: string[] = [];

  form = this.fb.group({
    vendorId: ["", [Validators.required]],
    warehouseId: ["", [Validators.required]],
    note: [""],
    attachments: this.fb.control<UploadedFile[]>([], { nonNullable: true }),
    items: this.fb.array([]),
    inspectItems: this.fb.array([]),
  });

  get isDraft(): boolean {
    return this.printing?.status === "Draft";
  }

  get isShipped(): boolean {
    return this.printing?.status === "Shipped";
  }

  get isInspected(): boolean {
    return this.printing?.status === "Inspected";
  }

  get items(): FormArray {
    return this.form.get("items") as FormArray;
  }

  get inspectItems(): FormArray {
    return this.form.get("inspectItems") as FormArray;
  }

  private createDraftItemForm(materialId = "", plannedQty = 1) {
    return this.fb.group({
      materialId: [materialId, [Validators.required]],
      plannedQty: [plannedQty, [Validators.required, Validators.min(1)]],
    });
  }

  private createInspectItemForm(item?: PrintingEntity["items"][number]) {
    return this.fb.group({
      materialId: [item?.materialId ?? "", [Validators.required]],
      receivedQty: [item?.receivedQty ?? 0, [Validators.required, Validators.min(0)]],
      acceptedQty: [item?.acceptedQty ?? 0, [Validators.required, Validators.min(0)]],
      rejectedQty: [item?.rejectedQty ?? 0, [Validators.required, Validators.min(0)]],
      inspectionNote: [item?.inspectionNote ?? ""],
    });
  }

  addDraftItem(): void {
    if (!this.isDraft) return;
    this.items.push(this.createDraftItemForm());
    this.materialOptionsByIndex.push([]);
    this.selectedMaterialIds.push("");
  }

  removeDraftItem(index: number): void {
    if (!this.isDraft || this.items.length <= 1) return;
    this.items.removeAt(index);
    this.materialOptionsByIndex.splice(index, 1);
    this.selectedMaterialIds.splice(index, 1);
  }

  async ngOnInit(): Promise<void> {
    if (!this.printingId) {
      await this.router.navigate(["/printings"]);
      return;
    }

    await this.loadPrinting(this.printingId);
  }

  private async loadPrinting(id: string): Promise<void> {
    const data = await this.printingEffects.getPrintingById(id);
    if (!data) {
      this.toastEffects.show("Printing not found.", "error");
      await this.router.navigate(["/printings"]);
      return;
    }

    setTimeout(() => {
      this.hydrateForm(data);
      this.isReady = true;
      this.cdr.detectChanges();
    }, 0);
  }

  async save(): Promise<void> {
    if (!this.isReady || this.isSaving || this.form.invalid || !this.printingId || !this.isDraft) {
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    try {
      const updated = await this.printingEffects.updatePrinting(this.printingId, {
        vendorId: (this.form.value.vendorId ?? "").trim(),
        warehouseId: (this.form.value.warehouseId ?? "").trim(),
        note: (this.form.value.note ?? "").trim(),
        items: (this.items.value as PrintingUpsertItem[]).map((item) => ({
          materialId: item.materialId,
          plannedQty: Number(item.plannedQty),
        })),
      } as UpdatePrintingRequest);

      setTimeout(() => {
        this.hydrateForm(updated);
        this.syncDraftControlState();
        this.toastEffects.show("Printing updated.", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update printing."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async inspect(): Promise<void> {
    if (!this.printingId || !this.isShipped) {
      return;
    }

    const inspectItems = this.inspectItems.value as InspectPrintingItemRequest[];
    if (!inspectItems) {
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    try {
      const updated = await this.printingEffects.inspectPrinting(this.printingId, {
        items: inspectItems,
        note: (this.form.value.note ?? "").trim(),
      });

      setTimeout(() => {
        this.printing = updated;
        this.toastEffects.show("Printing inspected and stock updated.", "success");
        this.cdr.detectChanges();
        this.router.navigate(["/printings"]).catch(() => undefined);
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to inspect printing."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/printings"]).catch(() => undefined);
  }

  async searchVendors(term: string): Promise<void> {
    const response = await this.vendorApi.searchVendors({ search: term, type: VendorType.Printing, page: 1, limit: 50 });
    const nextVendorOptions = this.mergeUniqueOptions(
      this.vendorOptions,
      response.data.map((item) => ({ value: item.id, label: item.name })),
    );

    this.deferViewMutation(() => {
      this.vendorOptions = nextVendorOptions;
    });
  }

  async searchWarehouses(term: string): Promise<void> {
    const response = await this.warehouseApi.searchWarehouses({ search: term, page: 1, limit: 50 });
    const nextWarehouseOptions = this.mergeUniqueOptions(
      this.warehouseOptions,
      response.data.map((item) => ({ value: item.id, label: item.name })),
    );

    this.deferViewMutation(() => {
      this.warehouseOptions = nextWarehouseOptions;
    });
  }

  async searchMaterials(term: string): Promise<void> {
    const response = await this.materialApi.searchMaterials({ search: term, page: 1, limit: 50 });
    const nextMaterialSearchOptions = response.data.map((item) => toMaterialAutocompleteOption(item));
    const nextMaterialOptionsByIndex = this.materialOptionsByIndex.map((seeded) =>
      this.mergeMaterialOptions(seeded, nextMaterialSearchOptions),
    );

    this.deferViewMutation(() => {
      this.materialSearchOptions = nextMaterialSearchOptions;
      this.materialOptionsByIndex = nextMaterialOptionsByIndex;
    });
  }

  onVendorChange(value: string | number | (string | number)[]): void {
    if (!this.isDraft) return;
    if (Array.isArray(value)) return;
    const selectedVendorId = value.toString();
    this.form.patchValue({ vendorId: selectedVendorId }, { emitEvent: false });
    this.selectedVendorId = selectedVendorId;
  }

  onWarehouseChange(value: string | number | (string | number)[]): void {
    if (!this.isDraft) return;
    if (Array.isArray(value)) return;
    const selectedWarehouseId = value.toString();
    this.form.patchValue({ warehouseId: selectedWarehouseId }, { emitEvent: false });
    this.selectedWarehouseId = selectedWarehouseId;
  }

  onMaterialChange(index: number, value: string | number | (string | number)[]): void {
    if (!this.isDraft) return;
    if (Array.isArray(value)) return;
    const selectedMaterialId = value.toString();
    this.items.at(index).patchValue({ materialId: selectedMaterialId }, { emitEvent: false });
    this.selectedMaterialIds[index] = selectedMaterialId;

    const selected =
      this.materialSearchOptions.find((option) => option.value.toString() === selectedMaterialId) ??
      this.materialOptionsByIndex[index]?.find((option) => option.value.toString() === selectedMaterialId);
    if (selected) {
      this.materialOptionsByIndex[index] = this.mergeMaterialOptions(this.materialOptionsByIndex[index] ?? [], [selected]);
    }
  }

  getInspectionMaterialLabel(index: number): string {
    const material = this.printing?.items?.[index]?.material;
    if (material?.name) {
      return `${material.name} (${material.sku || "-"})`;
    }

    const materialId = this.inspectItems.at(index)?.get("materialId")?.value;
    return (() => {
      if (typeof materialId === "string" && materialId.trim()) {
        return materialId;
      }
      return "-";
    })();
  }

  getInspectionPlannedQty(index: number): number {
    return this.printing?.items?.[index]?.plannedQty ?? 0;
  }

  getAttachmentHint(): string {
    if (this.isDraft) {
      return "Drag and drop one or more files here, or click to browse. Attachment changes save immediately.";
    }

    return "Attachments are read-only after draft.";
  }

  isAttachmentUploaderDisabled(): boolean {
    if (!this.isDraft) {
      return true;
    }

    if (this.isAttachmentSaving) {
      return true;
    }

    return false;
  }

  async onAttachmentsChange(attachments: UploadedFile[]): Promise<void> {
    if (!this.printingId || !this.isDraft || this.isAttachmentSaving) {
      return;
    }

    const previousAttachments = this.printing?.attachments ?? [];
    const previousAttachmentFileIds = this.getAttachmentFileIds(previousAttachments);
    const nextAttachmentFileIds = this.getAttachmentFileIds(attachments);
    if (this.areAttachmentFileIdsEqual(previousAttachmentFileIds, nextAttachmentFileIds)) {
      return;
    }

    const addedAttachments = attachments.filter((attachment) => !previousAttachmentFileIds.includes(attachment.id));

    this.isAttachmentSaving = true;
    this.cdr.detectChanges();
    try {
      const updated = await this.printingEffects.updatePrinting(this.printingId, { attachmentFileIds: nextAttachmentFileIds });

      this.deferViewMutation(() => {
        this.printing = updated;
        this.form.patchValue(
          {
            attachments: updated.attachments,
          },
          { emitEvent: false },
        );
      });
    } catch (exception: unknown) {
      await this.deleteUploadedFiles(addedAttachments);
      this.restoreAttachmentFiles(previousAttachments);
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update attachments."), "error");
    } finally {
      this.deferViewMutation(() => {
        this.isAttachmentSaving = false;
      });
    }
  }

  private syncDraftControlState(): void {
    const shouldEnableDraftFields = this.isDraft;
    const vendorControl = this.form.get("vendorId");
    const warehouseControl = this.form.get("warehouseId");
    const attachmentControl = this.form.get("attachments");

    if (shouldEnableDraftFields) {
      if (vendorControl?.disabled) {
        vendorControl.enable({ emitEvent: false });
      }
      if (warehouseControl?.disabled) {
        warehouseControl.enable({ emitEvent: false });
      }
      if (attachmentControl?.disabled) {
        attachmentControl.enable({ emitEvent: false });
      }
    } else {
      if (vendorControl?.enabled) {
        vendorControl.disable({ emitEvent: false });
      }
      if (warehouseControl?.enabled) {
        warehouseControl.disable({ emitEvent: false });
      }
      if (attachmentControl?.enabled) {
        attachmentControl.disable({ emitEvent: false });
      }
    }

    this.items.controls.forEach((itemControl) => {
      const materialControl = itemControl.get("materialId");
      const plannedQtyControl = itemControl.get("plannedQty");
      if (shouldEnableDraftFields) {
        if (materialControl?.disabled) {
          materialControl.enable({ emitEvent: false });
        }
        if (plannedQtyControl?.disabled) {
          plannedQtyControl.enable({ emitEvent: false });
        }
      } else {
        if (materialControl?.enabled) {
          materialControl.disable({ emitEvent: false });
        }
        if (plannedQtyControl?.enabled) {
          plannedQtyControl.disable({ emitEvent: false });
        }
      }
    });
  }

  private hydrateForm(data: PrintingEntity): void {
    const vendorId = this.toSelectedAutocompleteValue(data.vendorId);
    const warehouseId = this.toSelectedAutocompleteValue(data.warehouseId);
    this.printing = data;
    this.items.clear();
    this.inspectItems.clear();
    this.materialOptionsByIndex = [];
    this.selectedMaterialIds = [];
    for (const [index, item] of data.items.entries()) {
      this.items.push(this.createDraftItemForm(item.materialId, item.plannedQty));
      if (data.status === "Shipped" || data.status === "Inspected") {
        this.inspectItems.push(this.createInspectItemForm(item));
      }
      this.materialOptionsByIndex[index] = [];
      this.selectedMaterialIds[index] = item.materialId;
    }
    this.form.patchValue(
      {
        vendorId,
        warehouseId,
        note: data.note ?? "",
        attachments: data.attachments ?? [],
      },
      { emitEvent: false },
    );
    this.selectedVendorId = vendorId;
    this.selectedWarehouseId = warehouseId;
    this.syncDraftControlState();
    this.syncInspectionControlState();
    this.seedOptionsFromPrinting(data);
  }

  private syncInspectionControlState(): void {
    const isEditableInspection = this.isShipped;
    this.inspectItems.controls.forEach((inspectControl) => {
      const receivedQty = inspectControl.get("receivedQty");
      const acceptedQty = inspectControl.get("acceptedQty");
      const rejectedQty = inspectControl.get("rejectedQty");
      const inspectionNote = inspectControl.get("inspectionNote");

      if (isEditableInspection) {
        receivedQty?.enable({ emitEvent: false });
        acceptedQty?.enable({ emitEvent: false });
        rejectedQty?.enable({ emitEvent: false });
        inspectionNote?.enable({ emitEvent: false });
      } else {
        receivedQty?.disable({ emitEvent: false });
        acceptedQty?.disable({ emitEvent: false });
        rejectedQty?.disable({ emitEvent: false });
        inspectionNote?.disable({ emitEvent: false });
      }
    });
  }

  private seedOptionsFromPrinting(data: PrintingEntity): void {
    const seededVendors: AutocompleteOption[] = [];
    if (data.vendorId) {
      seededVendors.push({
        value: data.vendorId,
        label: data.vendor?.name?.trim() || data.vendorId,
      });
    }
    this.vendorOptions = this.mergeUniqueOptions(this.vendorOptions, seededVendors);

    const seededWarehouses: AutocompleteOption[] = [];
    if (data.warehouseId) {
      seededWarehouses.push({
        value: data.warehouseId,
        label: data.warehouse?.name?.trim() || data.warehouseId,
      });
    }
    this.warehouseOptions = this.mergeUniqueOptions(this.warehouseOptions, seededWarehouses);

    const seededMaterials = (data.items ?? [])
      .filter((item) => !!item.materialId)
      .map((item) => ({
        value: item.materialId,
        label: (() => {
          if (item.material?.name?.trim()) {
            return `${item.material.name} (${item.material?.sku?.trim() || "-"})`;
          }
          return item.materialId;
        })(),
        sequence: Number(item.material?.sequence ?? 0),
        name: item.material?.name ?? item.materialId,
        sku: item.material?.sku ?? "-",
      }));
    this.materialOptionsByIndex = (data.items ?? []).map((_, index) => {
      const seeded = seededMaterials[index];
      return (() => {
        if (seeded) {
          return [seeded];
        }
        return [];
      })();
    });
  }

  private mergeUniqueOptions(base: AutocompleteOption[], incoming: AutocompleteOption[]): AutocompleteOption[] {
    const map = new Map<string | number, AutocompleteOption>();
    [...base, ...incoming].forEach((option) => {
      if (!option.value) {
        return;
      }
      map.set(option.value, option);
    });

    return Array.from(map.values());
  }

  private mergeMaterialOptions(
    base: MaterialAutocompleteOption[],
    incoming: MaterialAutocompleteOption[],
  ): MaterialAutocompleteOption[] {
    return mergeMaterialAutocompleteOptions(base, incoming);
  }

  private deferViewMutation(callback: () => void): void {
    setTimeout(() => {
      callback();
      this.cdr.detectChanges();
    }, 0);
  }

  private toSelectedAutocompleteValue(value: string | null | undefined): string {
    if (typeof value === "string") {
      return value;
    }

    return "";
  }

  private getAttachmentFileIds(attachments: UploadedFile[] | null | undefined): string[] {
    if (!Array.isArray(attachments)) {
      return [];
    }

    return attachments
      .map((file) => {
        if (typeof file?.id === "string") {
          return file.id.trim();
        }

        return "";
      })
      .filter((attachmentFileId) => attachmentFileId.length > 0);
  }

  private restoreAttachmentFiles(attachments: UploadedFile[]): void {
    this.form.patchValue(
      {
        attachments,
      },
      { emitEvent: false },
    );
  }

  private async deleteUploadedFiles(files: UploadedFile[]): Promise<void> {
    for (const file of files) {
      if (!file.name) {
        continue;
      }

      try {
        await this.fileApi.deleteFile(file.name);
      } catch {
        continue;
      }
    }
  }

  private areAttachmentFileIdsEqual(previousAttachmentFileIds: string[], nextAttachmentFileIds: string[]): boolean {
    if (previousAttachmentFileIds.length !== nextAttachmentFileIds.length) {
      return false;
    }

    return previousAttachmentFileIds.every((attachmentFileId, index) => attachmentFileId === nextAttachmentFileIds[index]);
  }
}
